import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { notifyCreditsChanged } from '../utils/creditsSync'

export type ProActivationStatus = 'idle' | 'checking' | 'active' | 'timed_out'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 60_000

/**
 * After a Pro purchase, polls the user's profile row until is_pro === true.
 * Pro features are shown only once the backend has activated the plan.
 */
export function useProActivation(enabled: boolean) {
  const { user } = useAuth()
  const [status, setStatus] = useState<ProActivationStatus>(
    enabled ? 'checking' : 'idle',
  )
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!enabled) {
      setStatus('idle')
      return
    }
    if (!user?.id) return
    let cancelled = false
    let timer: number | undefined
    const startedAt = Date.now()
    setStatus('checking')

    const tick = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_pro')
        .eq('id', user.id)
        .single()
      if (cancelled) return
      if (!error && data?.is_pro === true) {
        setStatus('active')
        notifyCreditsChanged()
        return
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setStatus('timed_out')
        return
      }
      timer = window.setTimeout(() => void tick(), POLL_INTERVAL_MS)
    }
    void tick()

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [enabled, user?.id, attempt])

  return { status, retry: () => setAttempt((n) => n + 1) }
}
